'use client';
import { normalizeError } from '@korvo/core/lib/normalize-error';
import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';
import type React from 'react';
import type { ComponentType } from 'react';
import { useMemo, useRef } from 'react';

/** Resolve admin page export name on the server (e.g. server action). Client uses it to dynamic-import without receiving a function. */
export type GetModuleAdminExportName = (moduleName: string, pathStr: string) => Promise<string | null>;

/** Serializable session snapshot the app can pass so loaded admin pages can use it when present instead of useSession(). */
export type SerializableSession = {
  user?: { id?: string; name?: string; email?: string; role?: string; [k: string]: unknown };
  [k: string]: unknown;
};

export type ModuleAdminPageLoaderProps = {
  getModuleAdminExportName: GetModuleAdminExportName;
  onModuleNotFound?: (module: string, path: string) => void;
  /** Override when rendered from [...path] route (no [module] in params) */
  moduleName?: string;
  pathStr?: string;
  /** Optional: session snapshot from app; when present, loader passes it to the loaded admin page. */
  adminContext?: { session?: SerializableSession };
  /** Function to dynamically load a module's root exports. Provided by the app's generated registry. Required for admin loading. */
  loadModuleRoot: (moduleName: string) => Promise<Record<string, unknown>>;
};

/**
 * next/dynamic may invoke the async loader more than once when ancestors re-render
 * (e.g. session provider updates). Deduplicate by (module, path) so server actions
 * like getModuleAdminExportName are not spammed.
 */
const adminDynamicLoadPromises = new Map<
  string,
  Promise<{ default: ComponentType<{ adminContext?: ModuleAdminPageLoaderProps['adminContext'] }> }>
>();

function adminDynamicLoadCacheKey(moduleName: string, pathStr: string): string {
  return `${moduleName}\u0000${pathStr}`;
}

function LocalLoadingOverlay({ message }: { message: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--drawer-overlay-background)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        style={{
          padding: '1rem 1.5rem',
          borderRadius: '0.5rem',
          backgroundColor: 'white',
          boxShadow: '0 10px 25px rgba(15, 23, 42, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontSize: '0.875rem',
          color: 'var(--footer-foreground)',
        }}
      >
        <span
          style={{
            width: '1.25rem',
            height: '1.25rem',
            borderRadius: '9999px',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: 'var(--primary-default)',
            borderTopColor: 'transparent',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <span>{message}</span>
      </div>
    </div>
  );
}

/** Module-level loading screen for dynamic admin chunk. Avoids defining components inside ModuleAdminPageLoader. */
function AdminLoadingScreen({ message }: { message: string }) {
  return (
    <div
      style={{
        position: 'relative',
        height: 'calc(100vh - var(--topbar-height))',
        minHeight: 0,
      }}
    >
      <LocalLoadingOverlay message={message} />
    </div>
  );
}

/** Fixed-message loading fallback for next/dynamic; no props so it can be passed as loading: AdminLoadingFallback. */
function AdminLoadingFallback() {
  return <AdminLoadingScreen message="Loading admin..." />;
}

/** Module-level not-found content for admin loader. Avoids defining components inside callbacks. */
function AdminNotFoundContent({
  pathStr,
  moduleName,
  reason,
}: {
  pathStr: string;
  moduleName: string;
  reason: 'path' | 'unsupported' | 'export' | 'load-error';
}) {
  const messages: Record<typeof reason, { title: string; body: React.ReactNode }> = {
    path: {
      title: 'Admin Page Not Found',
      body: (
        <>
          The admin page &quot;{pathStr}&quot; for module &quot;{moduleName}&quot; could not be loaded.
        </>
      ),
    },
    unsupported: {
      title: 'Admin Page Not Found',
      body: <>Module &quot;{moduleName}&quot; is not supported for admin loading.</>,
    },
    export: {
      title: 'Admin Page Not Found',
      body: <>Export not found.</>,
    },
    'load-error': {
      title: 'Admin Page Not Found',
      body: (
        <>
          The admin page &quot;{pathStr}&quot; for module &quot;{moduleName}&quot; could not be loaded.
        </>
      ),
    },
  };
  const { title, body } = messages[reason];
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h1>{title}</h1>
      <p>{body}</p>
    </div>
  );
}

export function ModuleAdminPageLoader({
  getModuleAdminExportName,
  onModuleNotFound,
  moduleName: moduleProp,
  pathStr: pathStrProp,
  adminContext,
  loadModuleRoot,
}: ModuleAdminPageLoaderProps) {
  const params = useParams();
  const moduleFromParams = params?.module as string;
  const pathFromParams = params?.path as string[] | undefined;
  const module = moduleProp ?? moduleFromParams;
  const pathStr = pathStrProp ?? (pathFromParams ? pathFromParams.join('/') : '');
  const hasRoute = Boolean(module);
  const moduleName = module ? module.toLowerCase() : '';
  const getExportRef = useRef(getModuleAdminExportName);
  getExportRef.current = getModuleAdminExportName;
  const loadModuleRootRef = useRef(loadModuleRoot);
  loadModuleRootRef.current = loadModuleRoot;
  const onModuleNotFoundRef = useRef(onModuleNotFound);
  onModuleNotFoundRef.current = onModuleNotFound;
  const AdminComponent = useMemo(() => {
    if (!hasRoute) {
      return null;
    }
    return dynamic(
      () => {
        const cacheKey = adminDynamicLoadCacheKey(moduleName, pathStr);
        let promise = adminDynamicLoadPromises.get(cacheKey);
        if (!promise) {
          promise = (async () => {
            try {
              const exportName = await getExportRef.current(moduleName, pathStr);
              if (!exportName) {
                onModuleNotFoundRef.current?.(moduleName, pathStr);
                return {
                  default: function NotFoundPath() {
                    return (
                      <AdminNotFoundContent pathStr={pathStr} moduleName={moduleName} reason="path" />
                    );
                  },
                };
              }
              let mod: Record<string, unknown>;
              try {
                mod = await loadModuleRootRef.current(moduleName);
              } catch {
                onModuleNotFoundRef.current?.(moduleName, pathStr);
                return {
                  default: function NotFoundUnsupported() {
                    return (
                      <AdminNotFoundContent
                        pathStr={pathStr}
                        moduleName={moduleName}
                        reason="unsupported"
                      />
                    );
                  },
                };
              }
              const Component = mod[exportName];
              if (!Component) {
                onModuleNotFoundRef.current?.(moduleName, pathStr);
                return {
                  default: function NotFoundExport() {
                    return (
                      <AdminNotFoundContent pathStr={pathStr} moduleName={moduleName} reason="export" />
                    );
                  },
                };
              }
              return {
                default: Component as React.ComponentType<{
                  adminContext?: ModuleAdminPageLoaderProps['adminContext'];
                }>,
              };
            } catch (error: unknown) {
              const normalized = normalizeError(error);
              console.error(
                `Failed to load admin component for ${moduleName}/${pathStr}:`,
                normalized
              );
              onModuleNotFoundRef.current?.(moduleName, pathStr);
              return {
                default: function NotFoundLoadError() {
                  return (
                    <AdminNotFoundContent
                      pathStr={pathStr}
                      moduleName={moduleName}
                      reason="load-error"
                    />
                  );
                },
              };
            }
          })();
          adminDynamicLoadPromises.set(cacheKey, promise);
        }
        return promise;
      },
      {
        loading: AdminLoadingFallback,
        ssr: false,
      }
    );
  }, [hasRoute, moduleName, pathStr]);
  if (!hasRoute) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Admin Page Not Found</h1>
        <p>
          The admin page &quot;{pathStr || '(empty)'}&quot; for module &quot;{module || 'unknown'}&quot;
          could not be loaded.
        </p>
      </div>
    );
  }
  if (!AdminComponent) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Admin Page Not Found</h1>
        <p>
          The admin page &quot;{pathStr}&quot; for module &quot;{module || 'unknown'}&quot; could not
          be loaded.
        </p>
      </div>
    );
  }
  return <AdminComponent adminContext={adminContext} />;
}
